import type { ElementKind } from './types'
import type { StaffKey } from '../lib/staffText'

/** The left panel's sections, in the order they are listed. */
export type ToolGroup = 'live' | 'text' | 'brand' | 'shapes'

export const GROUPS: readonly ToolGroup[] = ['live', 'text', 'brand', 'shapes']

export const GROUP_LABEL: Record<ToolGroup, StaffKey> = {
  live: 'designs.tools.live',
  text: 'designs.tools.text',
  brand: 'designs.tools.brand',
  shapes: 'designs.tools.shapes',
}

/** Words and time are text; pictures, playlists and the logo are brand and media;
 *  a line is a shape; everything the queue fills in is live data. */
const GROUP_OF: Partial<Record<ElementKind, ToolGroup>> = {
  text: 'text',
  clock: 'text',
  dateTime: 'text',
  logo: 'brand',
  media: 'brand',
  image: 'brand',
  ticker: 'brand',
  rule: 'shapes',
  panel: 'shapes',
}

export function groupOf(kind: ElementKind): ToolGroup {
  return GROUP_OF[kind] ?? 'live'
}

/** The palette split into the left panel's groups, each in palette order; empty groups left out. */
export function toolGroups(kinds: readonly ElementKind[]): Array<{ group: ToolGroup; kinds: ElementKind[] }> {
  return GROUPS.map((group) => ({ group, kinds: kinds.filter((k) => groupOf(k) === group) })).filter((g) => g.kinds.length > 0)
}

/** Whether a tool's label answers a search: every word typed starts a word of the label. */
export function matchesSearch(label: string, query: string): boolean {
  const words = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean)
  if (words.length === 0) return true
  const own = label.toLocaleLowerCase().split(/[\s·,()-]+/).filter(Boolean)
  return words.every((w) => own.some((o) => o.startsWith(w)))
}

/** The tools left after a search, grouped and in order; groups left empty are dropped. */
export function searchTools<T extends { group: ToolGroup; label: string }>(
  tools: readonly T[],
  query: string,
): Array<{ group: ToolGroup; tools: T[] }> {
  const found = tools.filter((t) => matchesSearch(t.label, query))
  return GROUPS.map((group) => ({ group, tools: found.filter((t) => t.group === group) })).filter((g) => g.tools.length > 0)
}
